import { VgApiResult, vgApiRequest } from "./vg-api.service";
import {
  ACTIVATION_STATE,
  CONFIG_TYPE,
  PREPAYMENT_REQUIRED,
  QUEUE_ACTIVE,
} from "constants/bookingEnums";

/**
 * Booking configurations - Org_ServiceFulfilmentConfig_Table, called
 * ServiceFulfilmentConfig throughout the Core contracts.
 *
 * A configuration says how a service can be booked: how many can run per day,
 * how many resources each one needs, whether payment is taken up front. A
 * booking (service order) then references one.
 *
 * Production has 1,937 of these and 259 of its 324 service orders reference one,
 * so the shape here follows what is already in use rather than inventing it.
 */
export type BookingConfig = {
  serviceFulfilmentConfigId: number;
  orgId: number;
  serviceId: number;
  name: string;
  isActive: number;
  numberOfRequiredResources?: number;
  prePaymentRequired?: number;
  monetaryAmount?: number;
  maxOrdersPerDay?: number;
  maxDailyLimit?: number;
  configType?: number;
  isQueueActive?: number;
};

function num(value: unknown): number | undefined {
  return value != null ? Number(value) : undefined;
}

function pickConfig(raw: unknown): BookingConfig {
  const c = raw as Record<string, unknown>;
  const pick = (camel: string, pascal: string) => c[camel] ?? c[pascal];
  return {
    serviceFulfilmentConfigId: Number(
      pick("serviceFulfilmentConfigId", "ServiceFulfilmentConfigId") ?? 0
    ),
    orgId: Number(pick("orgId", "OrgId") ?? 0),
    serviceId: Number(pick("serviceId", "ServiceId") ?? 0),
    name: String(pick("name", "Name") ?? ""),
    isActive: Number(pick("isActive", "IsActive") ?? ACTIVATION_STATE.UNKNOWN),
    numberOfRequiredResources: num(
      pick("numberOfRequiredResources", "NumberOfRequiredResources")
    ),
    prePaymentRequired: num(pick("prePaymentRequired", "PrePaymentRequired")),
    monetaryAmount: num(pick("monetaryAmount", "MonetaryAmount")),
    maxOrdersPerDay: num(pick("maxOrdersPerDay", "MaxOrdersPerDay")),
    maxDailyLimit: num(pick("maxDailyLimit", "MaxDailyLimit")),
    configType: num(
      pick(
        "advancedOrBasicServiceFulfilmentConfig",
        "AdvancedOrBasicServiceFulfilmentConfig"
      )
    ),
    isQueueActive: num(pick("isQueueActive", "IsQueueActive")),
  };
}

type ConfigListResponse = {
  serviceFulfilmentConfigCompleteList?: unknown[];
  ServiceFulfilmentConfigCompleteList?: unknown[];
};

export async function readBookingConfigs(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<BookingConfig[]>> {
  const { data, error } = await vgApiRequest<ConfigListResponse>(
    accessToken,
    "Read_All_ServiceFulfilmentConfigs_By_Org_ID",
    { orgId }
  );
  if (error) return { data: null, error };
  const list =
    data?.serviceFulfilmentConfigCompleteList ??
    data?.ServiceFulfilmentConfigCompleteList ??
    [];
  return { data: list.map(pickConfig), error: null };
}

export type BookingConfigInput = {
  orgId: number;
  serviceId: number;
  name: string;
  isActive?: number;
  numberOfRequiredResources?: number;
  prePaymentRequired?: number;
  monetaryAmount?: number;
  maxOrdersPerDay?: number;
  maxDailyLimit?: number;
  configType?: number;
  isQueueActive?: number;
};

/**
 * All of these now persist.
 *
 * Until OlaCore 2026.8.226.851, Org_ServiceFulfilmentConfig_Table had no columns
 * for monetaryAmount, maxOrdersPerDay, maxDailyLimit, isQueueActive or the config
 * type - the contract accepted them and the insert dropped them silently.
 * Migration 50 added the columns and the write paths, so the form can ask for
 * them honestly. Requires that version or later.
 *
 * Duration is still not here: it lives per resource on
 * Org_ServiceFulfilmentConfigResourceMap_Table, which is where a "30 minute
 * consultation" gets its 30 minutes.
 *
 * Every enum starts at 1 = Unknown, so omitting one stores Unknown rather than
 * defaulting sensibly. Defaults are explicit.
 */
/**
 * Two fields here are not what the screen suggests, and both are refused
 * silently by the platform's validation - the request comes back "Parameter
 * Invalid" with no field named, which is what stopped any configuration from
 * being created at all.
 *
 * contactId must be sent as 0. A configuration belongs to an organisation or to
 * a contact, never both, and the contract defaults contactId to -1 - so leaving
 * it out is not "unset", it is "contact minus one", and every clinic-level
 * configuration was rejected for it.
 *
 * numberOfRequiredResources must be 0 for anyone but the system user. Resources
 * are attached to a configuration afterwards through the resource map, which is
 * also where their durations live. Sending the 1 the form defaulted to was
 * refused every time.
 */
function toPayload(payload: BookingConfigInput) {
  return {
    orgId: payload.orgId,
    contactId: 0,
    serviceId: payload.serviceId,
    name: payload.name,
    isActive: payload.isActive ?? ACTIVATION_STATE.ACTIVE,
    numberOfRequiredResources: 0,
    prePaymentRequired:
      payload.prePaymentRequired ?? PREPAYMENT_REQUIRED.NOT_REQUIRED,
    monetaryAmount: payload.monetaryAmount ?? 0,
    maxOrdersPerDay: payload.maxOrdersPerDay ?? 0,
    maxDailyLimit: payload.maxDailyLimit ?? 0,
    advancedOrBasicServiceFulfilmentConfig:
      payload.configType ?? CONFIG_TYPE.BASIC,
    isQueueActive: payload.isQueueActive ?? QUEUE_ACTIVE.NOT_ACTIVE,
  };
}

export async function createBookingConfig(
  accessToken: string,
  payload: BookingConfigInput
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(
    accessToken,
    "Create_ServiceFulfilmentConfig",
    toPayload(payload)
  );
}

export async function updateBookingConfig(
  accessToken: string,
  payload: BookingConfigInput & { serviceFulfilmentConfigId: number }
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_Org_ServiceFulfilmentConfig", {
    ...toPayload(payload),
    serviceFulfilmentConfigId: payload.serviceFulfilmentConfigId,
  });
}

/**
 * The booking configurations shared by every clinic - the seeded consultations.
 *
 * These have no organisation, which is exactly why readBookingConfigs cannot
 * return them: it filters on orgId. A clinic that had built nothing of its own
 * therefore saw "no active booking configurations" while the platform ones sat
 * there unasked for.
 */
export async function readPlatformBookingConfigs(
  accessToken: string
): Promise<VgApiResult<BookingConfig[]>> {
  const { data, error } = await vgApiRequest<{
    serviceFulfilmentConfigList?: unknown[];
    ServiceFulfilmentConfigList?: unknown[];
  }>(accessToken, "Read_All_Platform_ServiceFulfilmentConfigs", {});

  if (error) return { data: null, error };
  const list =
    data?.serviceFulfilmentConfigList ?? data?.ServiceFulfilmentConfigList ?? [];
  return { data: list.map(pickConfig), error: null };
}

/**
 * Everything this clinic can book against: its own configurations and the
 * platform's.
 *
 * A failed platform read does not fail the whole call - the clinic's own
 * configurations are still bookable, and the reverse is the case that matters
 * most, since a new clinic has nothing but the platform's.
 */
export async function readBookableConfigs(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<BookingConfig[]>> {
  const [own, platform] = await Promise.all([
    readBookingConfigs(accessToken, orgId),
    readPlatformBookingConfigs(accessToken),
  ]);

  if (own.error && platform.error) return { data: null, error: own.error };

  // Platform ones last: a clinic's own service is the more likely choice, and a
  // configuration it built should not be buried under the defaults.
  return { data: [...(own.data ?? []), ...(platform.data ?? [])], error: null };
}
